import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";

import {
  fetchSubFrasesThunk,
  createSubFraseThunk,
  updateSubFraseThunk,
  toggleSubFraseActiveThunk,
  fetchTiposFortalezaThunk,
  fetchTiposSubFraseThunk,
} from "../../store/preguntasTalento/preguntasTalento.thunks";
import {
  getFrasesSelectorService,
} from "../../services/preguntasTalento/preguntasTalento.service";
import { getTalentosSelectoresService } from "../../services/talentos/talentos.service";

import Alert from "../UI/Alert";
import Loader from "../UI/Loader";
import EmptyState from "../UI/EmptyState";
import Pagination from "../UI/Pagination";
import SearchInput from "../UI/SearchInput";
import Modal from "../UI/Modal";
import Select2 from "../UI/Select2";

const emptyItem = () => ({ texto: "", orden: 1 });
const emptyForm = {
  frase: "",
  talento: "",
  tipo: "fortaleza",
  is_active: true,
  items: [emptyItem()],
};
const PAGE_SIZE = 10;

const SubFrasesLayer = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s) => s.preguntasTalento.subFrases);
  const tipos = useSelector((s) => s.preguntasTalento.tiposFortaleza);
  const tiposSubFrase = useSelector((s) => s.preguntasTalento.tiposSubFrase);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterTipoSubFrase, setFilterTipoSubFrase] = useState("");
  const [filterFrase, setFilterFrase] = useState("");
  const [filterTalento, setFilterTalento] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [confirmToggle, setConfirmToggle] = useState(null);

  useEffect(() => {
    dispatch(fetchTiposFortalezaThunk());
    dispatch(fetchTiposSubFraseThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSubFrasesThunk({
      page,
      search,
      tipo_fortaleza: filterTipo || undefined,
      tipo: filterTipoSubFrase || undefined,
      frase: filterFrase || undefined,
      talento: filterTalento || undefined,
    }));
  }, [dispatch, page, search, filterTipo, filterTipoSubFrase, filterFrase, filterTalento]);

  useEffect(() => { setPage(1); }, [search, filterTipo, filterTipoSubFrase, filterFrase, filterTalento]);

  // Select2 fetchers: toman search del input y devuelven [{id,label}]
  const fetchTipos = useMemo(
    () => (q = "") => {
      const items = tipos.map((t) => ({ id: String(t.id), label: t.label }));
      if (!q) return Promise.resolve(items);
      const qq = q.toLowerCase();
      return Promise.resolve(items.filter((i) => i.label.toLowerCase().includes(qq)));
    },
    [tipos]
  );

  const fetchTiposSubFrase = useMemo(
    () => (q = "") => {
      const items = tiposSubFrase.map((t) => ({ id: String(t.id), label: t.label }));
      if (!q) return Promise.resolve(items);
      const qq = q.toLowerCase();
      return Promise.resolve(items.filter((i) => i.label.toLowerCase().includes(qq)));
    },
    [tiposSubFrase]
  );

  const fetchFrases = useCallback(
    (searchTerm = "") =>
      getFrasesSelectorService({ search: searchTerm }),
    []
  );

  const fetchFrasesFiltradasPorTipo = useCallback(
    (tipo) => (searchTerm = "") =>
      getFrasesSelectorService({
        search: searchTerm,
        tipo_fortaleza: tipo || undefined,
      }),
    []
  );

  const fetchTalentos = useCallback(
    (searchTerm = "") => getTalentosSelectoresService(searchTerm),
    []
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, items: [emptyItem()] });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      frase: row.frase ? String(row.frase) : "",
      talento: row.talento ? String(row.talento) : "",
      tipo: row.tipo || "fortaleza",
      is_active: row.is_active,
      items: [{ texto: row.texto, orden: row.orden }],
    });
    setFormError(null);
    setModalOpen(true);
  };

  const updateItem = (idx, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  const addItem = () => {
    setForm((f) => ({
      ...f,
      items: [...f.items, { ...emptyItem(), orden: f.items.length + 1 }],
    }));
  };

  const removeItem = (idx) => {
    setForm((f) => ({
      ...f,
      items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
    }));
  };

  const refetchSubFrases = () => {
    dispatch(fetchSubFrasesThunk({
      page, search,
      tipo_fortaleza: filterTipo || undefined,
      tipo: filterTipoSubFrase || undefined,
      frase: filterFrase || undefined,
      talento: filterTalento || undefined,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.frase || !form.talento) {
      setFormError("Debes seleccionar una frase y un talento");
      return;
    }
    const items = form.items.map((it) => ({
      texto: (it.texto || "").trim(),
      orden: Number(it.orden) || 1,
    }));
    if (items.some((it) => !it.texto)) {
      setFormError("Todas las sub-frases deben tener texto");
      return;
    }
    setSaving(true);

    if (editing) {
      const payload = {
        frase: Number(form.frase),
        talento: Number(form.talento),
        tipo: form.tipo || "fortaleza",
        texto: items[0].texto,
        orden: items[0].orden,
        is_active: form.is_active,
      };
      const result = await dispatch(updateSubFraseThunk({ id: editing.id, payload }));
      setSaving(false);
      if (result.meta.requestStatus === "fulfilled") {
        setModalOpen(false);
        refetchSubFrases();
      } else {
        setFormError(result.payload || "No se pudo guardar");
      }
      return;
    }

    const base = {
      frase: Number(form.frase),
      talento: Number(form.talento),
      tipo: form.tipo || "fortaleza",
      is_active: form.is_active,
    };
    let okCount = 0;
    for (let i = 0; i < items.length; i++) {
      const result = await dispatch(createSubFraseThunk({ ...base, ...items[i] }));
      if (result.meta.requestStatus === "fulfilled") {
        okCount += 1;
      } else {
        setSaving(false);
        setFormError(
          `${result.payload || "No se pudo guardar"} (sub-frase #${i + 1}` +
          (okCount ? `, ${okCount} ya guardada${okCount === 1 ? "" : "s"})` : ")")
        );
        if (okCount) refetchSubFrases();
        return;
      }
    }
    setSaving(false);
    setModalOpen(false);
    refetchSubFrases();
  };

  const handleToggle = async () => {
    const row = confirmToggle;
    setConfirmToggle(null);
    await dispatch(toggleSubFraseActiveThunk(row.id));
    dispatch(fetchSubFrasesThunk({
      page, search,
      tipo_fortaleza: filterTipo || undefined,
      tipo: filterTipoSubFrase || undefined,
      frase: filterFrase || undefined,
      talento: filterTalento || undefined,
    }));
  };

  return (
    <div className="card p-0 radius-12">
      <div className="card-body p-24">
        <div className="mb-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-auto">
              <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <Icon icon="mdi:format-list-bulleted-type" width="22" />
                Sub-frases
              </h5>
            </div>
            <div className="col-12 col-md">
              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-md-end flex-wrap">
                <div style={{ minWidth: 180 }}>
                  <Select2
                    value={filterTipo}
                    onChange={setFilterTipo}
                    fetchOptions={fetchTipos}
                    placeholder="Fortaleza"
                  />
                </div>
                <div style={{ minWidth: 160 }}>
                  <Select2
                    value={filterTipoSubFrase}
                    onChange={setFilterTipoSubFrase}
                    fetchOptions={fetchTiposSubFrase}
                    placeholder="Tipo"
                  />
                </div>
                <div style={{ minWidth: 200 }}>
                  <Select2
                    value={filterFrase}
                    onChange={setFilterFrase}
                    fetchOptions={fetchFrasesFiltradasPorTipo(filterTipo)}
                    placeholder="Frase"
                  />
                </div>
                <div style={{ minWidth: 180 }}>
                  <Select2
                    value={filterTalento}
                    onChange={setFilterTalento}
                    fetchOptions={fetchTalentos}
                    placeholder="Talento"
                  />
                </div>
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Buscar sub-frases..."
                  minChars={3}
                />
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-1"
                  onClick={openCreate}
                >
                  <Icon icon="mdi:plus" width="18" />
                  <span>Nueva sub-frase</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <Alert message={String(error)} variant="error" />}

        {loading ? (
          <Loader center text="Cargando sub-frases..." delay={200} />
        ) : data.results.length === 0 ? (
          <EmptyState
            icon={search ? "mdi:magnify-remove-outline" : "mdi:format-list-bulleted-type"}
            title={search ? "No se encontraron sub-frases" : "Aún no hay sub-frases registradas"}
            description={search ? "Intenta con otra búsqueda" : "Dale click a \"Nueva sub-frase\" para crear la primera"}
          />
        ) : (
          <>
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table mb-0" style={{ minWidth: 1000 }}>
                <thead>
                  <tr>
                    <th scope="col" style={{ minWidth: 60 }}>ID</th>
                    <th scope="col" style={{ minWidth: 280 }}>Sub-frase</th>
                    <th scope="col" style={{ minWidth: 240 }}>Frase</th>
                    <th scope="col" style={{ minWidth: 160 }}>Talento</th>
                    <th scope="col" style={{ minWidth: 140 }}>Fortaleza</th>
                    <th scope="col" style={{ minWidth: 120 }}>Tipo</th>
                    <th scope="col" style={{ minWidth: 100 }}>Estado</th>
                    <th scope="col" style={{ minWidth: 160 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((row) => (
                    <tr key={row.id} className={!row.is_active ? "text-secondary-light" : ""}>
                      <td>{row.id}</td>
                      <td>{row.texto}</td>
                      <td>{row.frase_texto}</td>
                      <td>{row.talento_nombre}</td>
                      <td>{row.tipo_fortaleza_label || "—"}</td>
                      <td>
                        <span className={`badge ${row.tipo === "aplicacion" ? "bg-info-focus text-info-main" : "bg-warning-focus text-warning-main"}`}>
                          {row.tipo_label || (row.tipo === "aplicacion" ? "Aplicación" : "Fortaleza")}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${row.is_active ? "bg-success-focus text-success-main" : "bg-neutral-200 text-secondary-light"}`}>
                          {row.is_active ? "Alta" : "Anulada"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                            onClick={() => openEdit(row)}
                          >
                            <Icon icon="mdi:pencil-outline" width="16" />
                            Editar
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm d-flex align-items-center gap-1 ${
                              row.is_active ? "btn-outline-danger" : "btn-outline-success"
                            }`}
                            onClick={() => setConfirmToggle(row)}
                          >
                            <Icon icon={row.is_active ? "mdi:close-circle-outline" : "mdi:check-circle-outline"} width="16" />
                            {row.is_active ? "Anular" : "Reactivar"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              count={data.count}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editing ? "Editar sub-frase" : "Alta de sub-frase"}
        size="lg"
      >
        {saving ? (
          <Loader center type="dots" text="Guardando..." />
        ) : (
          <form onSubmit={handleSubmit}>
            {formError && <Alert message={String(formError)} variant="error" />}

            <div className="row g-3">
              <div className="col-12 col-md-6">
                <Select2
                  label="Frase"
                  required
                  value={form.frase}
                  onChange={(v) => setForm({ ...form, frase: v })}
                  fetchOptions={fetchFrases}
                  placeholder="Selecciona una frase"
                />
                <small className="text-secondary-light">
                  La fortaleza se toma automáticamente de la frase.
                </small>
              </div>

              <div className="col-12 col-md-6">
                <Select2
                  label="Talento"
                  required
                  value={form.talento}
                  onChange={(v) => setForm({ ...form, talento: v })}
                  fetchOptions={fetchTalentos}
                  placeholder="Selecciona un talento"
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label fw-medium">
                  Tipo <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  required
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                >
                  {tiposSubFrase.length === 0 ? (
                    <>
                      <option value="fortaleza">Fortaleza</option>
                      <option value="aplicacion">Aplicación</option>
                    </>
                  ) : (
                    tiposSubFrase.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="mt-3 mb-2 d-flex justify-content-between align-items-center">
              <label className="form-label fw-medium mb-0">
                {editing ? "Sub-frase" : "Sub-frases"} <span className="text-danger">*</span>
              </label>
              {!editing && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                  onClick={addItem}
                >
                  <Icon icon="mdi:plus" width="16" />
                  Agregar sub-frase
                </button>
              )}
            </div>

            {form.items.map((it, idx) => (
              <div key={idx} className="border rounded-3 p-3 mb-3 bg-neutral-50">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-medium text-secondary-light">
                    {editing ? "Texto" : `Sub-frase #${idx + 1}`}
                  </span>
                  {!editing && form.items.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger d-flex align-items-center gap-1 p-0"
                      onClick={() => removeItem(idx)}
                    >
                      <Icon icon="mdi:trash-can-outline" width="16" />
                      Quitar
                    </button>
                  )}
                </div>

                <textarea
                  className="form-control"
                  rows={3}
                  maxLength={500}
                  required
                  value={it.texto}
                  onChange={(e) => updateItem(idx, { texto: e.target.value })}
                  placeholder="Texto de la sub-frase"
                />
              </div>
            ))}

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setModalOpen(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                {editing
                  ? "Guardar cambios"
                  : `Dar de alta${form.items.length > 1 ? ` (${form.items.length})` : ""}`}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        title={confirmToggle?.is_active ? "Anular sub-frase" : "Reactivar sub-frase"}
        size="sm"
      >
        <p className="text-secondary-light">
          ¿Seguro que quieres {confirmToggle?.is_active ? "anular" : "reactivar"} la sub-frase{" "}
          <strong className="text-primary">{confirmToggle?.texto}</strong>?
        </p>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setConfirmToggle(null)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={`btn ${confirmToggle?.is_active ? "btn-danger" : "btn-success"}`}
            onClick={handleToggle}
          >
            {confirmToggle?.is_active ? "Anular" : "Reactivar"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SubFrasesLayer;
